/**
 * IntegrationMappingPanel — shared field mapping panel for all integrations.
 *
 * Replaces the near-identical ShopifyPanel, AccelaPanel, and any future
 * integration panel. The only integration-specific values are passed as props:
 *   - fieldKey        the raw API key name ("ShopifyField", "accelaField", etc.)
 *   - fieldLabel      column header shown to admins ("Shopify Field", etc.)
 *   - apiPrefix       URL segment ("Shopify/Integrations", "Accela/Integrations")
 *   - getFieldsPath   endpoint for fetching available fields ("GetShopifyFields")
 *   - scopeOptions    list of scope/record type strings for the selector
 *
 * Internal state always uses the normalized `IntegrationFieldMap.field` key.
 * Serialization to/from the API maps `field` ↔ `fieldKey` transparently.
 */

import useApi from "@/hooks/useApi";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { SingleValue } from "react-select";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import { AlertTriangle, IconClose } from "@velaro/velaro-component-lib";
import CustomSelect from "../CustomSelect";
import IntegrationMappingRow, { Option } from "./IntegrationMappingRow";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Normalized mapping row — all integrations use this internally. */
export interface IntegrationFieldMap {
  id: number;
  /** Normalized: the selected integration field as a react-select Option. */
  field: SingleValue<Option>;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  defaultValue: string;
  scopeType: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IntegrationMappingPanelProps {
  /** Panel starts with this scope type selected. */
  scopeType: string;
  /** The config ID for this integration instance. */
  selectedConfigId: number;
  /** Needed to resolve the config list hook internally if used; pass 0 if unused. */
  teamId: number;
  /** Callback to surface "All changes saved" indicator in the parent. */
  showSavedMessage: () => void;
  /** Disable the "Delete Record" button when this is the only panel. */
  isDeleteDisabled: boolean;

  // -- Integration-specific config --

  /** Raw key name sent to/received from the API, e.g. "ShopifyField". */
  fieldKey: string;
  /** Column header, e.g. "Shopify Field". */
  fieldLabel: string;
  /** URL prefix for all API calls, e.g. "Shopify/Integrations". */
  apiPrefix: string;
  /** Path used to fetch available fields, e.g. "GetShopifyFields". */
  getFieldsPath: string;
  /** Scope / record type options shown in the panel header selector. */
  scopeOptions: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function IntegrationMappingPanel(props: IntegrationMappingPanelProps) {
  const {
    fieldKey,
    fieldLabel,
    apiPrefix,
    getFieldsPath,
    scopeOptions: scopeOptionsList,
    selectedConfigId,
    showSavedMessage,
    isDeleteDisabled
  } = props;

  const api = useApi();

  const [mappedFields, setMappedFields] = useState<IntegrationFieldMap[]>([]);
  const [scopeType, setScopeType] = useState<string>(props.scopeType);
  const [metadata, setMetadata] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Scope-level delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [scopeTypeToDelete, setScopeTypeToDelete] = useState<string | null>(null);

  // Field-level delete modal
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{ index: number; fieldName: string } | null>(null);

  // Guard against auto-save on initial render
  const isInitialRender = useRef(true);

  // ── fetch available fields for the current scope ──────────────────────────
  useEffect(() => {
    async function fetchMetadata() {
      if (!scopeType || selectedConfigId == null) {
        setMetadata([]);
        return;
      }
      setIsLoading(true);
      try {
        const resp = await api.messaging.post(
          `${apiPrefix}/${getFieldsPath}/${selectedConfigId}`,
          scopeType
        );
        const json = await resp.json();
        const newOptions = Object.values(json).map((key) => ({
          value: key,
          label: key as string
        })) as Option[];
        setMetadata(newOptions);
      } catch (err) {
        console.error(`${fieldLabel}: failed to load fields`, err);
        setMetadata([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchMetadata();
  }, [api.messaging, scopeType, selectedConfigId, apiPrefix, getFieldsPath, fieldLabel]);

  // ── fetch saved mappings for the current scope ────────────────────────────
  const fetchMapping = useCallback(async () => {
    if (selectedConfigId == null || !scopeType) return;
    try {
      const resp = await api.messaging.get(
        `${apiPrefix}/Mapping/${scopeType}/${selectedConfigId}`
      );
      if (!resp.ok) {
        console.error(`${fieldLabel}: failed to load mapping`, resp.statusText);
        return;
      }
      const json = await resp.json();
      // Normalize: API key → internal `field` property
      const normalized: IntegrationFieldMap[] = json.map((el: any) => ({
        ...el,
        field: el[fieldKey] != null
          ? { value: el[fieldKey], label: el[fieldKey] }
          : { value: "", label: "" }
      }));
      setMappedFields(normalized);
    } catch (err) {
      console.error(`${fieldLabel}: error loading mapping`, err);
      setMappedFields([]);
    }
  }, [api.messaging, scopeType, selectedConfigId, apiPrefix, fieldKey, fieldLabel]);

  useEffect(() => {
    if (!scopeType) return;
    fetchMapping();
  }, [fetchMapping, scopeType]);

  // ── auto-save on every change (skip initial render) ───────────────────────
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    saveRecord();
  }, [mappedFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── save all rows ─────────────────────────────────────────────────────────
  async function saveRecord() {
    if (selectedConfigId == null) return;
    try {
      const payload = mappedFields.map((el) => ({
        [fieldKey]: el.field?.value ?? "",
        id: el.id,
        create: el.create,
        usedForSearching: el.usedForSearching,
        showWhenSelected: el.showWhenSelected,
        showInSearch: el.showInSearch,
        defaultValue: el.defaultValue,
        scopeType: el.scopeType
      }));
      await api.messaging.post(
        `${apiPrefix}/Mapping/${selectedConfigId}`,
        { Mappings: payload }
      );
    } catch (err) {
      console.error(`${fieldLabel}: save failed`, err);
    }
  }

  // ── delete a single mapping row ───────────────────────────────────────────
  async function deleteRecord(id: number) {
    try {
      await api.messaging.delete(`${apiPrefix}/Mapping/${id}`);
    } catch (err) {
      console.error(`${fieldLabel}: delete failed`, err);
    }
  }

  // ── add a new empty row ───────────────────────────────────────────────────
  const addRow = async () => {
    const newMap: IntegrationFieldMap = {
      id: -1,
      field: { value: "", label: "" },
      create: false,
      usedForSearching: false,
      showWhenSelected: false,
      showInSearch: false,
      defaultValue: "",
      scopeType
    };
    setMappedFields((prev) => [...prev, newMap]);

    // Fetch real DB id for the new row after a brief delay
    setTimeout(async () => {
      try {
        const resp = await api.messaging.get(`${apiPrefix}/Mapping/`);
        const lastId = await resp.json();
        setMappedFields((prev) =>
          prev.map((f) => (f.id === -1 ? { ...f, id: lastId } : f))
        );
      } catch (err) {
        console.error(`${fieldLabel}: failed to fetch new row id`, err);
      }
    }, 1000);
  };

  // ── update a single row in state ──────────────────────────────────────────
  const updateMappedField = (index: number, mappedField: IntegrationFieldMap) => {
    setMappedFields((prev) => {
      const clone = [...prev];
      clone[index] = mappedField;
      return clone;
    });
  };

  // ── field-level delete ────────────────────────────────────────────────────
  const handleDeleteFieldClick = (index: number) => {
    const fieldName = mappedFields[index]?.field?.label || "this field";
    setFieldToDelete({ index, fieldName });
    setIsDeleteFieldModalOpen(true);
  };

  const confirmDeleteField = () => {
    if (fieldToDelete === null) return;
    const { index } = fieldToDelete;
    if (mappedFields[index]?.id !== -1) {
      deleteRecord(mappedFields[index].id);
    }
    setMappedFields((prev) => {
      const clone = [...prev];
      clone.splice(index, 1);
      return clone;
    });
    setIsDeleteFieldModalOpen(false);
    setFieldToDelete(null);
  };

  // ── scope-level delete ────────────────────────────────────────────────────
  const handleDeleteRecordClick = (scope: string) => {
    setScopeTypeToDelete(scope);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRecord = async () => {
    if (!scopeTypeToDelete) return;
    try {
      await api.messaging.delete(
        `${apiPrefix}/Mapping/DeleteRecord/${scopeTypeToDelete}`
      );
      setMappedFields((prev) =>
        prev.filter((f) => f.scopeType !== scopeTypeToDelete)
      );
    } catch (err) {
      console.error(`${fieldLabel}: delete record failed`, err);
    }
    setIsDeleteModalOpen(false);
    setScopeTypeToDelete(null);
  };

  // ── render ────────────────────────────────────────────────────────────────
  const iconClassName =
    "transition fa-solid fa-angle-down" + (expanded ? " fa-rotate-180" : "");

  return (
    <div className="panel bg-slate-gray-50 dark:bg-stone-300 rounded-md shadow-md mb-4">
      {/* Panel header */}
      <div
        className="panel-header flex items-center h-16 px-4 cursor-pointer"
        onClick={() => setExpanded((s) => !s)}
      >
        <div
          className="text-slate-50 dark:text-white font-medium text-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <CustomSelect
            options={scopeOptionsList}
            value={scopeType}
            width="w-72"
            onChange={setScopeType}
          />
        </div>
        <div className="ml-auto dark:text-white">
          <button
            type="button"
            className="h-8 w-8 text-slate-400"
            onClick={() => setExpanded((s) => !s)}
            aria-label={expanded ? "Collapse panel" : "Expand panel"}
          >
            <i className={iconClassName}></i>
          </button>
        </div>
      </div>

      {/* Panel body */}
      {expanded && (
        <div className="panel-body rounded-2xl animated fadeIn dark:border-stone-100 p-4">
          <table className="p-4 w-full bg-white">
            <thead>
              <tr>
                <th className="pb-4 p-2 pl-4 w-80 text-center">{fieldLabel}</th>
                <th className="pb-4 p-2 w-80 text-center">Default Value</th>
                <th className="pb-4 p-2 w-24 text-center">Create</th>
                <th className="pb-4 p-2 w-24 text-center">Used For Searching</th>
                <th className="pb-4 p-2 w-24 text-center">Show When Selected</th>
                <th className="pb-4 p-2 w-24 text-center">Show In Search</th>
                <th className="pb-4 p-2 w-24 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {mappedFields.map((mappedField, i) => (
                <IntegrationMappingRow
                  key={i}
                  mappedField={mappedField}
                  i={i}
                  fieldLabel={fieldLabel}
                  updateMappedField={updateMappedField}
                  isLoading={isLoading}
                  deleteRow={handleDeleteFieldClick}
                  metadata={metadata}
                  showSavedMessage={showSavedMessage}
                />
              ))}
            </tbody>
          </table>

          {scopeType && (
            <div className="flex justify-end pt-4 gap-3">
              <button
                type="button"
                className={`rounded-lg items-center px-6 py-2 border text-center text-sm font-medium ${
                  isDeleteDisabled || mappedFields.length === 0
                    ? "bg-slate-100 border-slate-100 text-slate-300 cursor-not-allowed"
                    : "bg-white border-primary hover:border-primary-hover text-primary"
                }`}
                onClick={() => handleDeleteRecordClick(scopeType)}
                disabled={isDeleteDisabled || mappedFields.length === 0}
              >
                Delete Record
              </button>
              <button
                type="button"
                className="rounded-lg items-center px-6 py-2 bg-white border border-primary hover:border-primary-hover text-center text-primary text-sm font-medium"
                onClick={addRow}
              >
                Add Field
              </button>
            </div>
          )}
        </div>
      )}

      {/* Scope-level delete confirmation */}
      {isDeleteModalOpen && (
        <Modal show={isDeleteModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">Delete Scope Type</div>
              </div>
              <button type="button" onClick={() => setIsDeleteModalOpen(false)} aria-label="Close">
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>
            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to delete all field mappings for{" "}
              <strong>{scopeTypeToDelete}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-primary hover:border-primary-hover text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteRecord}
                className="rounded-lg px-6 py-2 bg-primary border border-primary hover:bg-primary-hover text-white text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}

      {/* Field-level delete confirmation */}
      {isDeleteFieldModalOpen && (
        <Modal show={isDeleteFieldModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">Delete Field Mapping</div>
              </div>
              <button
                type="button"
                onClick={() => setIsDeleteFieldModalOpen(false)}
                aria-label="Close"
              >
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>
            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to delete the field mapping for{" "}
              <strong>{fieldToDelete?.fieldName}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setIsDeleteFieldModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-primary hover:border-primary-hover text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteField}
                className="rounded-lg px-6 py-2 bg-primary border border-primary hover:bg-primary-hover text-white text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
